import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";

import Button from "@/components/Button";
import { FormLayout } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import { api } from "@/utils/api";
import FormField from "@/components/Forms/FormField";

type FormDataT = {
  article_id: string;
};

interface Props {
  selectedProducts: number[];
  setSelectedProducts: React.Dispatch<React.SetStateAction<number[]>>;
  onClose: () => void;
}

const ProductsPostModal: FC<Props> = ({
  onClose,
  selectedProducts,
  setSelectedProducts,
}) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const { id } = useParams();

  const methods = useForm<FormDataT>();
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await api.post(`/v1/google-amazon-product/upload`, {
        ...data,
        ids: selectedProducts,
      });

      await queryClient.invalidateQueries(["google_amazon_products", { id }], {
        exact: false,
      });

      alerts.success("Selected products were successfully uploaded");

      setSelectedProducts([]);

      onClose();
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <SidePanel heading="Upload Products to Shopping List" onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                control={control}
                name="article_id"
                type="text"
                label="Article ID"
                placeholder="Add ID"
              />
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Upload
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ProductsPostModal;
